import {
  Grid,
  IconButton,
  Slider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useCurrentStore, useOrdering } from 'hooks'
import { ChangeEventHandler, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { SubmitOrderSection } from './SubmitOrderSection'
import { KMoneyField } from 'components/core/KMoneyField'
import { KFieldLabel } from 'components/core/KFieldLabel'
import { KToggleButton } from 'components/core/KButton'

interface OrderTipsState {
  tipIndex?: number
  tipPercentForDriver?: number
  deliveryNote?: string
  generalNote?: string
}

const defaultTipNote =
  'Thank you for supporting your local businesses. You are making a huge difference in helping this community.'

export const OrderTips = () => {
  const { t } = useTranslation()
  const theme = useTheme()

  const { store, formatCurrency } = useCurrentStore()
  const { order, isSplitTip, saveOrderProps } = useOrdering()

  const [state, setState] = useState<OrderTipsState>({
    deliveryNote: '',
    generalNote: '',
    tipPercentForDriver:
      order?.default_tip_for_driver ??
      store?.tip_settings?.default_tip_for_driver,
  })

  const tipSettings = store?.tip_settings
  const orderType = order?.order_type

  useEffect(() => {
    if (!tipSettings || !orderType) return

    const generalNote =
      tipSettings.note_for_online_ordering ?? t(defaultTipNote)
    const deliveryNote = orderType === 'delivery' ? tipSettings.note : ''
    setState((state) => ({
      ...state,
      tipIndex: tipSettings.default_tip_guide,
      generalNote,
      deliveryNote,
    }))
  }, [tipSettings, orderType, t])

  const handleTipGuideClick = (tipPercent: number, tipIndex: number) => {
    setState((state) => ({ ...state, tipIndex: tipIndex }))
    saveOrderProps({ tip_percent: tipPercent })
  }

  const onTipChange: ChangeEventHandler<HTMLInputElement> = (event: any) => {
    if (!order) return
    const tipAmount = Number(event.target.value)
    const tipPercent = tipAmount / order.subtotal
    const tipGuideIndex = store?.tip_settings?.tip_guides?.findIndex(
      (tg) => tipPercent - tg < 0.001,
    )
    if (tipGuideIndex !== state.tipIndex) {
      setState((state) => ({ ...state, tipIndex: tipGuideIndex }))
    }
    saveOrderProps({ tip: tipAmount, tip_percent: undefined })
  }

  const onChangeTipForDriver = (newValue: any) => {
    if (isSplitTip) {
      const tipPercent = Number(newValue)

      setState({ ...state, tipPercentForDriver: tipPercent })
      saveOrderProps({ default_tip_for_driver: tipPercent })
    }
  }

  const onResetTip = () => {
    setState((state) => ({ ...state, tipIndex: undefined }))
    saveOrderProps({ tip_percent: undefined, tip: undefined })
  }

  if (!store || !order) return null

  return (
    <SubmitOrderSection label={t('Tips')}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <KMoneyField
            label={t('Tips amount')}
            value={order.tip ?? 0}
            onChange={onTipChange}
            endAdornment={
              order.tip !== undefined &&
              order.tip > 0 && (
                <IconButton onClick={onResetTip}>
                  <CloseIcon />
                </IconButton>
              )
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KFieldLabel>{t('Popular choices')}</KFieldLabel>
          <Stack direction="row" spacing={2} sx={{ marginY: 1 }}>
            {store.tip_settings?.tip_guides?.map((tipPercent, index) => [
              <KToggleButton
                size="medium"
                key={tipPercent}
                variant={index === state.tipIndex ? 'primary' : 'secondary'}
                onClick={() => handleTipGuideClick(tipPercent, index)}
              >
                {`${(tipPercent * 100).toFixed(2)}%`}
              </KToggleButton>,
            ])}
          </Stack>
        </Grid>
        {isSplitTip &&
          state.tipPercentForDriver !== undefined &&
          state.tipPercentForDriver !== null && (
            <Grid
              sx={{
                paddingTop: theme.spacing(2.5),
                paddingLeft: theme.spacing(2.5),
              }}
              container
            >
              <Grid item xs={12}>
                <Slider
                  defaultValue={state.tipPercentForDriver}
                  valueLabelDisplay="on"
                  step={10}
                  marks
                  min={0}
                  max={100}
                  onChange={(_, val) => onChangeTipForDriver(val)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <Trans
                    defaults="Tip for Delivery Driver ({{tipPercentForDriver}}%): {{tipForDriver}}"
                    values={{
                      tipPercentForDriver: state.tipPercentForDriver,
                      tipForDriver: formatCurrency(order.tip_for_driver ?? 0),
                    }}
                  />
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  <Trans
                    defaults="Tip for Staff ({{tipPercentForStaff}}%): {{tipForStaff}}"
                    values={{
                      tipPercentForStaff: 100 - state.tipPercentForDriver,
                      tipForStaff: formatCurrency(
                        order.tip_for_restaurant_staff ?? 0,
                      ),
                    }}
                  />
                </Typography>
              </Grid>
            </Grid>
          )}
        {state.deliveryNote && (
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="secondary">
              {state.deliveryNote}
            </Typography>
          </Grid>
        )}
        {state.generalNote && (
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="secondary">
              {state.generalNote}
            </Typography>
          </Grid>
        )}
      </Grid>
    </SubmitOrderSection>
  )
}
